import { memo, useMemo, useEffect, useState, useCallback } from 'react';
import { Button, Price } from '@divlab/divanui';

import * as ApiPecom from '@Api/Pecom';
import * as Meta from '@Queries/Meta';
import { useDeps } from '@Contexts/DI';
import Link from '@Navigation/Link';
import useTranslation from '@Queries/useTranslation';
import Img from '@UI/Img';
import images from './images';
import GallerySection from '@Components/GallerySection';
import ProductCard from './elements/ProductCard';
import IconBasket from './elements/IconBasket';
import styles from './CartBlock.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { CartData } from '@Types/Cart';

export interface DeliveryCost {
  courierSum: number;
  pickupSum: number;
}

interface CartBlockProps extends HTMLAttributes<HTMLDivElement> {
  cart: CartData;
  type?: 'pickupPoint' | 'toAddress';
  title?: string;
}

const defaultProductIds = [20831];

const CartBlock: FC<CartBlockProps> = (props) => {
  const { cart, type, title, ...restProps } = props;
  const { logger } = useDeps();
  const { t } = useTranslation();
  const [deliveryCost, setDeliveryCost] = useState(null);
  const meta = Meta.useMeta();

  const goodsInfo = useMemo(() => {
    const result = [];

    if (cart.positions?.length > 0) {
      cart.positions.forEach((position) => {
        position.products.forEach((product) => {
          result.push({
            id: product.id,
            quantity: product.quantity,
          });
        });
      });
    } else {
      defaultProductIds.forEach((producId) => {
        result.push({
          id: producId,
          quantity: 1,
        });
      });
    }

    return result;
  }, [cart.positions]);

  const deliveryInfo = useMemo(() => {
    const isCourier = type === 'toAddress';

    if (!deliveryCost) return null;

    return {
      text: isCourier ? t('ui.cart.delivery.to-adress') : t('ui.cart.delivery.to-pickup-point'),
      price: isCourier ? deliveryCost.courierSum : deliveryCost.pickupSum,
    };
  }, [deliveryCost, type, t]);

  // Получаем информацию о стоимости доставки до подьезда
  const load = useCallback(async () => {
    if (goodsInfo.length < 1) return;

    try {
      const options = {
        goodsInfo,
        receiverCityInfo: meta.data.region.name,
      };
      const [courierSum, pickupSum] = await Promise.all([
        ApiPecom.getDeliveryCost({
          ...options,
          isDelivery: true,
          courierAddress: `${meta.data.region.name}, улица Ленина, д. 1`,
        }),
        ApiPecom.getDeliveryCost({
          ...options,
          isDelivery: false,
          courierAddress: '',
        }),
      ]);

      setDeliveryCost({ courierSum, pickupSum });
    } catch (err) {
      logger.log(err);
    }
  }, [goodsInfo, meta.data, logger]);

  useEffect(() => {
    if (!meta.data.region.isPec || !cart) return;

    load();
  }, [cart, load, meta.data]);

  return (
    <div {...restProps}>
      <div className={styles.container}>
        <div className={styles.content}>
          <Link className={styles.link} to='/order/check'>
            <Button className={styles.button} wide>
              <IconBasket className={styles.basket} />
              <span>{t('ui.cart.create-order')}</span>
              <span>•</span>
              <Price price={cart.cost} />
            </Button>
          </Link>

          {cart.positions?.length > 0 && (
            <GallerySection
              cnSlide={styles.slide}
              cnGallery={styles.gallery}
              title={title}
              slides={cart.positions}
              renderSlide={(position) => <ProductCard position={position} />}
              alignment='bottom'
              inContainer={false}
            />
          )}
        </div>

        {deliveryInfo && (
          <div className={styles.deliveryInfo}>
            <Img src={images.pec} className={styles.pecIcon} cnImage={styles.pecImg} />
            <div className={styles.deliveryText}>
              {`${deliveryInfo.text} `}
              <Price price={deliveryInfo.price} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CartBlock);
